<template>
  <div>
    <BasePopper
      v-click-outside="turnOff"
      :display="isOn"
      :arrow="false"
      :placement="placement"
      :position-over-trigger="true"
    >
      <div :class="classObject" class="btn-group">
        <BaseButton
          class="main-button pull-left"
          :color="colorScheme"
          :disabled="disabled || disabledMainButton"
          :outline="outline"
          :data-test="dataTest + '-button'"
          @click="$emit('main-button-action')"
        >
          <!-- @slot Content inside button -->
          <slot />
        </BaseButton>
        <BaseButton
          data-toggle="dropdown"
          class="btn-caret pull-right"
          :color="colorScheme"
          :disabled="disabled || disabledDropdown"
          :outline="outline"
          :data-test="dataTest + '-dropdown'"
          @click="toggle"
        >
          <span class="caret" />
          <span class="sr-only">{{ $t('Toggle Dropdown') }}</span>
        </BaseButton>
      </div>
      <template #popper>
        <div class="dropdown">
          <!-- @slot Content for dropdown -->
          <slot name="dropdown" />
        </div>
      </template>
    </BasePopper>
  </div>
</template>

<script>
/* eslint-disable import/extensions */
import BasePopper from '@/components/BasePopper.vue'
import BaseButton from '@/components/BaseButton.vue'
import {
  validateColorScheme,
  validateSizeName,
  validatePopperPlacement,
} from '@/utils/propValidation'

/**
 * Button with dropdown <br>
 * Be sure to provide any element (a, span, div, etc), not just text in the slot <br>
 * $attrs passed to `<base-button>`
 */
export default {
  components: { BasePopper, BaseButton },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledMainButton: {
      type: Boolean,
      default: false,
    },
    disabledDropdown: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'sm',
      required: false,
      validator(value) {
        return validateSizeName(value)
      },
    },
    placement: {
      type: String,
      default: 'bottom-end',
      required: false,
      validator(value) {
        return validatePopperPlacement(value)
      },
    },
    colorScheme: {
      type: String,
      default: 'default',
      required: false,
      validator(value) {
        return validateColorScheme(value)
      },
    },
    outline: {
      type: Boolean,
      default: false,
      required: false,
    },
    dataTest: {
      type: String,
      default: 'button-split',
    },
  },
  emits: ['main-button-action'],
  data() {
    return {
      isOn: false,
    }
  },
  computed: {
    classObject() {
      return {
        [`btn-group-${this.size}`]: true,
        outline: this.outline,
      }
    },
  },
  methods: {
    toggle() {
      return this.isOn ? this.turnOff() : this.turnOn()
    },
    turnOn() {
      this.isOn = true
    },
    turnOff() {
      this.isOn = false
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-group.outline {
  @apply tw-shadow-none;
}

.btn .caret {
  margin-top: 0; // overwrite app_1.min.scss style
}

.btn-group-lg > .btn {
  padding: 10px 16px;
}

.dropdown {
  right: 0; // safari bug where dropdown isnt correctly positioned
}

.main-button.btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

// Select first-child of the button to apply padding so the whole button is clickable

// Step 1: Overwrite normal btn padding
.btn-group-sm,
.btn-group-xs,
.btn-group-md,
.btn-group-lg {
  .btn {
    padding: 0;
  }
}

// Step 2: Adjust padding of the child buttons

:deep(.btn-group-xs .main-button.btn) > :first-child,
.btn-group-xs .btn-caret {
  padding: 4px 10px;
}

:deep(.btn-group-sm .main-button.btn) > :first-child,
.btn-group-sm .btn-caret {
  padding: 3px 6px;
}

:deep(.btn-group-md .main-button.btn) > :first-child,
.btn-group-md .btn-caret {
  padding: 6px 12px;
}

:deep(.btn-group-lg .main-button.btn) > :first-child,
.btn-group-lg .btn-caret {
  padding: 10px 16px;
}
</style>
